import React, { useState, useEffect } from 'react';

/**
 * Renders tool execution results based on tool type
 */
export const ToolResultRenderer = ({ tool, toolResult, conversationData, onFieldUpdate }) => {
  if (!toolResult) {
    return <div className="ov-tool-loading">Loading tool...</div>;
  }

  const toolType = toolResult.toolType || 'rag_only';

  switch (toolType) {
    case 'scoring_calculator':
      return <ScoringCalculatorRenderer tool={tool} result={toolResult} onFieldUpdate={onFieldUpdate} />;
    
    case 'matrix':
      return <MatrixRenderer tool={tool} result={toolResult} onFieldUpdate={onFieldUpdate} />;
    
    case 'quiz':
      return <QuizRenderer tool={tool} result={toolResult} />;
    
    case 'checklist':
      return <ChecklistRenderer tool={tool} result={toolResult} />;
    
    case 'computed':
      return <ComputedToolRenderer tool={tool} result={toolResult} />;
    
    case 'rag_only':
    default:
      return <RAGToolRenderer tool={tool} result={toolResult} />;
  }
};

/**
 * Scoring Calculator Renderer
 * Example: 3-Lever Execution Accelerator, Stress Load Index
 */
const ScoringCalculatorRenderer = ({ tool, result, onFieldUpdate }) => {
  const [fieldValues, setFieldValues] = useState({});
  const [score, setScore] = useState(null);
  const [recommendation, setRecommendation] = useState(null);

  // Initialize field values from result
  useEffect(() => {
    const initialValues = {};
    result.fields?.forEach(field => {
      if (field.value !== null && field.value !== undefined) {
        initialValues[field.name] = field.value;
      }
    });
    setFieldValues(initialValues);
    
    // If all fields have values, calculate score
    if (result.score !== null && result.score !== undefined) {
      setScore(result.score);
      setRecommendation(result.recommendation);
    }
  }, [result]);

  const handleFieldChange = (fieldName, value) => {
    const newValues = { ...fieldValues, [fieldName]: parseFloat(value) || 0 };
    setFieldValues(newValues);
    
    if (onFieldUpdate) {
      onFieldUpdate(fieldName, value);
    }
    
    // Recalculate score if all fields filled
    const allFilled = result.fields.every(f => 
      newValues[f.name] !== null && newValues[f.name] !== undefined
    );
    
    if (allFilled) {
      const total = result.fields.reduce((sum, f) => {
        const val = parseFloat(newValues[f.name]) || 0;
        return sum + val;
      }, 0);
      setScore(total);
      
      // Find recommendation
      const rec = findRecommendation(total, result.recommendations);
      setRecommendation(rec);
    } else {
      setScore(null);
      setRecommendation(null);
    }
  };

  const findRecommendation = (score, recommendations) => {
    if (!recommendations) return null;
    
    for (const [range, message] of Object.entries(recommendations)) {
      if (matchesRange(score, range)) {
        return { range, message };
      }
    }
    return null;
  };

  const matchesRange = (score, range) => {
    const rangeMatch = range.match(/(\d+)-(\d+)|<(\d+)|>(\d+)/);
    if (rangeMatch) {
      if (rangeMatch[1]) {
        // Range: "12-15"
        return score >= parseFloat(rangeMatch[1]) && score <= parseFloat(rangeMatch[2]);
      } else if (rangeMatch[3]) {
        // Less than: "<5"
        return score < parseFloat(rangeMatch[3]);
      } else if (rangeMatch[4]) {
        // Greater than: ">10"
        return score > parseFloat(rangeMatch[4]);
      }
    }
    return false;
  };

  return (
    <div className="ov-tool-scoring-calculator">
      {result.missingFields && result.missingFields.length > 0 && (
        <div className="ov-tool-missing-fields">
          <p className="ov-tool-missing-label">Please provide:</p>
          <ul>
            {result.missingFields.map((field, idx) => (
              <li key={idx}>{field.label || field.name}</li>
            ))}
          </ul>
        </div>
      )}
      
      <div className="ov-tool-scoring-fields">
        {result.fields?.map((field, idx) => (
          <div key={idx} className="ov-tool-scoring-field">
            <label className="ov-tool-field-label">
              {field.label || field.name}
              {field.value !== null && field.value !== undefined && (
                <span className="ov-tool-field-value-badge">✓ {field.value}</span>
              )}
            </label>
            <div className="ov-tool-field-input-group">
              <input
                type="number"
                min={field.min !== undefined ? field.min : 0}
                max={field.max !== undefined ? field.max : undefined}
                value={fieldValues[field.name] || ''}
                onChange={(e) => handleFieldChange(field.name, e.target.value)}
                placeholder={field.max !== undefined ? `${field.min || 0}-${field.max}` : `min: ${field.min || 0}`}
                className="ov-tool-field-input"
              />
              {field.max !== undefined ? (
                <span className="ov-tool-field-range">
                  {field.min || 0} - {field.max}
                </span>
              ) : (
                <span className="ov-tool-field-range">
                  {field.min !== undefined ? `min: ${field.min}` : 'any value'}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
      
      {score !== null && (
        <div className="ov-tool-score-result">
          <div className="ov-tool-score-display">
            <span className="ov-tool-score-label">Execution Score:</span>
            <span className="ov-tool-score-value">{score}</span>
          </div>
          
          {recommendation && (
            <div className="ov-tool-recommendation">
              <h4>Recommendation</h4>
              <p className="ov-tool-recommendation-text">{recommendation.message}</p>
              <span className="ov-tool-recommendation-range">Score: {recommendation.range}</span>
            </div>
          )}
        </div>
      )}
      
      {result.actions && result.actions.length > 0 && (
        <div className="ov-modal-actions">
          <h4>Next Steps</h4>
          <ul>
            {result.actions.map((action, idx) => (
              <li key={idx}>{action}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

/**
 * Matrix Renderer (2x2, priority matrix, etc.)
 */
const MatrixRenderer = ({ tool, result, onFieldUpdate }) => {
  const [axisValues, setAxisValues] = useState({});

  useEffect(() => {
    const initialValues = {};
    result.axes?.forEach(axis => {
      if (axis.value !== null && axis.value !== undefined) {
        initialValues[axis.name] = axis.value;
      }
    });
    setAxisValues(initialValues);
  }, [result]);

  const handleAxisChange = (axisName, value) => {
    setAxisValues({ ...axisValues, [axisName]: parseFloat(value) || 0 });
    if (onFieldUpdate) {
      onFieldUpdate(axisName, value);
    }
  };

  if (result.axes && result.axes.length >= 2) {
    const xAxis = result.axes[0];
    const yAxis = result.axes[1];
    const xValue = axisValues[xAxis.name];
    const yValue = axisValues[yAxis.name];
    
    // Calculate quadrant dynamically
    const calculateQuadrant = () => {
      if (xValue === null || xValue === undefined || yValue === null || yValue === undefined) {
        return null;
      }
      const xMidpoint = ((xAxis.min || 1) + (xAxis.max || 10)) / 2;
      const yMidpoint = ((yAxis.min || 1) + (yAxis.max || 10)) / 2;
      const quadrantKey = 
        (xValue >= xMidpoint ? 'high' : 'low') + '_' +
        (yValue >= yMidpoint ? 'high' : 'low');
      return result.quadrants?.[quadrantKey] || result.quadrants?.default || 'No specific guidance for this quadrant.';
    };
    
    const currentQuadrant = calculateQuadrant();
    const hasBothValues = xValue !== null && xValue !== undefined && yValue !== null && yValue !== undefined;
    
    return (
      <div className="ov-tool-matrix">
        {result.missingAxes && result.missingAxes.length > 0 && (
          <div className="ov-tool-missing-fields">
            <p>Please provide: {result.missingAxes.map(a => a.label || a.name).join(' and ')}</p>
          </div>
        )}
        
        <div className="ov-tool-matrix-inputs">
          {result.axes.map((axis, idx) => (
            <div key={idx} className="ov-tool-matrix-axis">
              <label>{axis.label || axis.name}</label>
              <input
                type="number"
                min={axis.min || 1}
                max={axis.max || 10}
                value={axisValues[axis.name] || ''}
                onChange={(e) => handleAxisChange(axis.name, e.target.value)}
                placeholder={`${axis.min || 1}-${axis.max || 10}`}
              />
            </div>
          ))}
        </div>
        
        {hasBothValues && currentQuadrant && (
          <div className="ov-tool-matrix-result">
            <div className="ov-tool-quadrant">
              <h4>Your Quadrant: {currentQuadrant.includes('Do Now') ? 'Do Now' : 
                                   currentQuadrant.includes('Schedule') ? 'Schedule' :
                                   currentQuadrant.includes('Delegate') ? 'Delegate' :
                                   currentQuadrant.includes('Remove') ? 'Remove' : 'Your Position'}</h4>
              <p>{typeof currentQuadrant === 'string' ? currentQuadrant : (currentQuadrant.message || currentQuadrant)}</p>
            </div>
          </div>
        )}
      </div>
    );
  }
  
  return <div>Matrix configuration incomplete</div>;
};

/**
 * Quiz Renderer
 */
const QuizRenderer = ({ tool, result }) => {
  const [answers, setAnswers] = useState({});
  
  return (
    <div className="ov-tool-quiz">
      {result.questions?.map((question, qIdx) => (
        <div key={qIdx} className="ov-tool-quiz-question">
          <h4>{question.question}</h4>
          <div className="ov-tool-quiz-options">
            {question.options?.map((option, oIdx) => (
              <button
                key={oIdx}
                className={`ov-tool-quiz-option ${answers[qIdx] === oIdx ? 'selected' : ''}`}
                onClick={() => setAnswers({ ...answers, [qIdx]: oIdx })}
              >
                {option}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

/**
 * Checklist Renderer
 */
const ChecklistRenderer = ({ tool, result }) => {
  const [checked, setChecked] = useState({});
  
  return (
    <div className="ov-tool-checklist">
      {result.items?.map((item, idx) => (
        <label key={idx} className="ov-tool-checklist-item">
          <input
            type="checkbox"
            checked={checked[idx] || false}
            onChange={(e) => setChecked({ ...checked, [idx]: e.target.checked })}
          />
          <span>{item}</span>
        </label>
      ))}
    </div>
  );
};

/**
 * Computed Tool Renderer (for tools with custom compute functions)
 * Example: ROI Calculator, custom calculators
 */
const ComputedToolRenderer = ({ tool, result }) => {
  return (
    <div className="ov-tool-computed-content">
      {result.metrics && Object.keys(result.metrics).length > 0 && (
        <div className="ov-tool-metrics-display">
          <h4>Results</h4>
          <div className="ov-tool-metrics-grid">
            {Object.entries(result.metrics).map(([key, value]) => (
              <div key={key} className="ov-tool-metric-item">
                <span className="ov-tool-metric-label">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                <span className="ov-tool-metric-value">{value}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {result.actions && result.actions.length > 0 && (
        <div className="ov-modal-actions">
          <h4>Recommended Actions</h4>
          <ul>
            {result.actions.map((action, idx) => (
              <li key={idx}>{action}</li>
            ))}
          </ul>
        </div>
      )}
      
      {result.followUp && (
        <div className="ov-tool-followup">
          <p>{result.followUp}</p>
        </div>
      )}
      
      {!result.metrics && !result.actions && (
        <div className="ov-tool-computed-placeholder">
          <p>Tool executed successfully. Check the conversation for results.</p>
        </div>
      )}
    </div>
  );
};

/**
 * RAG Tool Renderer (fallback)
 */
const RAGToolRenderer = ({ tool, result }) => {
  return (
    <div className="ov-tool-rag-content">
      {result.content && (
        <div className="ov-tool-rag-text">
          {typeof result.content === 'string' ? (
            <p>{result.content}</p>
          ) : (
            <pre>{JSON.stringify(result.content, null, 2)}</pre>
          )}
        </div>
      )}
      
      {result.actions && result.actions.length > 0 && (
        <div className="ov-modal-actions">
          <h4>How to Use This Framework</h4>
          <ul>
            {result.actions.map((action, idx) => (
              <li key={idx}>{action}</li>
            ))}
          </ul>
        </div>
      )}
      
      {!result.content && (
        <div className="ov-tool-rag-placeholder">
          <p>This framework is available in your knowledge base. Ask questions about it in the chat!</p>
        </div>
      )}
    </div>
  );
};

export default ToolResultRenderer;

